const {
  FREQ_DAILY,
  FREQ_WEEKLY,
  FREQ_MONTHLY,
  FREQ_YEARLY,
  MONTHLY_MODE_DAY_OF_MONTH,
  MONTHLY_MODE_NTH_WEEKDAY,
  END_MODE_NEVER,
  END_MODE_ON,
  END_MODE_AFTER,
  describeRule,
  presetsForDate,
  ruleFromDate,
} = require("./recurrence-rule.js");

const UNIT_DAY = "day";
const UNIT_WEEK = "week";
const UNIT_MONTH = "month";
const UNIT_YEAR = "year";

const UNIT_BY_FREQUENCY = {
  [FREQ_DAILY]: UNIT_DAY,
  [FREQ_WEEKLY]: UNIT_WEEK,
  [FREQ_MONTHLY]: UNIT_MONTH,
  [FREQ_YEARLY]: UNIT_YEAR,
};

const FREQUENCY_BY_UNIT = {
  [UNIT_DAY]: FREQ_DAILY,
  [UNIT_WEEK]: FREQ_WEEKLY,
  [UNIT_MONTH]: FREQ_MONTHLY,
  [UNIT_YEAR]: FREQ_YEARLY,
};

function anchorParts(anchorTimestamp) {
  const anchor = new Date(Number(anchorTimestamp) * 1000);
  const monthDay = anchor.getDate();
  const seeded = presetsForDate(anchorTimestamp).find(preset => preset.key === "monthly");

  return {
    monthDay,
    weekPosition: seeded.rule.nthWeekday.position,
    weekPositionDay: seeded.rule.nthWeekday.day,
  };
}

function toPickerValue(rule, anchorTimestamp = 0) {
  if (!rule) {
    return null;
  }

  const anchor = anchorTimestamp ? anchorParts(anchorTimestamp) : null;
  const nthWeekday = rule.nthWeekday || {};

  return {
    unit: UNIT_BY_FREQUENCY[rule.freq],
    interval: rule.interval,
    weekdays: [...rule.byDay || []],
    monthlyMode: rule.monthlyMode || MONTHLY_MODE_DAY_OF_MONTH,
    monthDay: rule.monthDay || (anchor ? anchor.monthDay : 0),
    weekPosition: nthWeekday.position || (anchor ? anchor.weekPosition : 1),
    weekPositionDay: nthWeekday.day || (anchor ? anchor.weekPositionDay : "MO"),
    end: {
      mode: rule.end.mode,
      date: rule.end.mode === END_MODE_ON ? rule.end.until : 0,
      count: rule.end.mode === END_MODE_AFTER ? rule.end.count : 0,
    },
  };
}

function fromPickerValue(value) {
  if (!value) {
    return null;
  }

  const freq = FREQUENCY_BY_UNIT[value.unit];
  const isNthWeekday = value.monthlyMode === MONTHLY_MODE_NTH_WEEKDAY;

  return {
    freq,
    interval: value.interval,
    byDay: freq === FREQ_WEEKLY ? [...value.weekdays || []] : [],
    monthlyMode: freq === FREQ_MONTHLY ? value.monthlyMode : null,
    monthDay: isNthWeekday ? 0 : value.monthDay,
    nthWeekday: isNthWeekday
      ? { position: value.weekPosition, day: value.weekPositionDay }
      : null,
    end: {
      mode: value.end.mode,
      until: value.end.mode === END_MODE_ON ? value.end.date : 0,
      count: value.end.mode === END_MODE_AFTER ? value.end.count : 0,
    },
  };
}

function pickerPresets(anchorTimestamp) {
  return presetsForDate(anchorTimestamp).map(preset => ({
    key: preset.key,
    label: preset.label,
    value: toPickerValue(preset.rule, anchorTimestamp),
  }));
}

function pickerDefaultValue(anchorTimestamp) {
  return toPickerValue(ruleFromDate(anchorTimestamp), anchorTimestamp);
}

function pickerSummary(rule, anchorTimestamp) {
  return describeRule(rule, anchorTimestamp);
}

function monthlyModeOptions(anchorTimestamp) {
  const anchor = anchorParts(anchorTimestamp);

  const dayOfMonthRule = {
    freq: FREQ_MONTHLY,
    interval: 1,
    monthlyMode: MONTHLY_MODE_DAY_OF_MONTH,
    monthDay: anchor.monthDay,
    end: { mode: END_MODE_NEVER },
  };

  const nthWeekdayRule = {
    freq: FREQ_MONTHLY,
    interval: 1,
    monthlyMode: MONTHLY_MODE_NTH_WEEKDAY,
    nthWeekday: { position: anchor.weekPosition, day: anchor.weekPositionDay },
    end: { mode: END_MODE_NEVER },
  };

  return [
    {
      value: MONTHLY_MODE_DAY_OF_MONTH,
      label: describeRule(dayOfMonthRule, anchorTimestamp),
    },
    {
      value: MONTHLY_MODE_NTH_WEEKDAY,
      label: describeRule(nthWeekdayRule, anchorTimestamp),
    },
  ];
}

module.exports = {
  UNIT_DAY,
  UNIT_WEEK,
  UNIT_MONTH,
  UNIT_YEAR,
  toPickerValue,
  fromPickerValue,
  pickerPresets,
  pickerDefaultValue,
  pickerSummary,
  monthlyModeOptions,
};
